import { Link } from "react-router";
import {
  ArrowRight,
  Calendar,
  ShieldCheck,
  Monitor,
  Upload,
  Clock,
} from "lucide-react";
import logoLight from "@exiftrator/shared/assets/logo_light.svg";
import logoDark from "@exiftrator/shared/assets/logo_dark.svg";
import { useTheme } from "@exiftrator/shared/contexts";
import { ThemeToggle } from "@exiftrator/shared/components";
import { usePageTitle } from "@exiftrator/shared/hooks";
import { formatPageTitle } from "@exiftrator/shared/utils";
import { Badge } from "@exiftrator/shared/components/ui";

export default function FixScannedPhotoDatesPage() {
  usePageTitle(formatPageTitle("Fix Scanned Photo Dates"));

  const { theme } = useTheme();

  return (
    <div className="min-h-screen bg-bg-deep text-text-primary">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 border-b border-border-default/60 bg-bg-deep/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/">
            <img
              src={theme === "dark" ? logoDark : logoLight}
              alt="Exiftrator"
              className="h-9 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Link
              to="/app"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-primary text-primary-foreground transition-all duration-200 hover:opacity-90 hover:gap-3"
            >
              Open App
              <ArrowRight className="size-3.5" />
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-linear-to-b from-bg-deep via-bg-primary/50 to-bg-deep pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 pt-20 pb-16 sm:pt-28 sm:pb-20">
          <div className="max-w-2xl mx-auto text-center">
            <Badge variant="secondary" className="mb-8 gap-2">
              <Calendar className="size-3.5" />
              Fix Scanned Photo Dates
            </Badge>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight text-text-primary mb-6">
              Fix Wrong Dates on
              <br />
              <span className="text-text-secondary">Scanned & Analog Photos</span>
            </h1>

            <p className="text-base sm:text-lg text-text-secondary leading-relaxed mb-10 max-w-xl mx-auto">
              Scanned film photos and digitized prints often carry the scan date
              instead of the original date taken. Exiftrator's Calendar View
              lets you visually correct timestamps in bulk — entirely in your
              browser, with zero uploads.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/app"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-lg bg-primary text-primary-foreground transition-all duration-200 hover:opacity-90 hover:gap-3 shadow-md"
              >
                Launch Web App — Free
                <ArrowRight className="size-4" />
              </Link>
              <Link
                to="#download"
                className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-lg border border-border-default text-text-secondary transition-all duration-200 hover:bg-bg-hover hover:text-text-primary"
              >
                <Monitor className="size-3.5" />
                Download Desktop Beta
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ────────────────────────────────────────────── */}
      <section className="bg-bg-primary border-t border-border-default">
        <div className="max-w-5xl mx-auto px-6 py-16 sm:py-24">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-text-primary text-center mb-14">
            How to fix scanned photo dates
          </h2>

          <div className="grid sm:grid-cols-3 gap-8 max-w-3xl mx-auto">
            {[
              {
                icon: <Upload className="size-5" />,
                number: "01",
                title: "Drop your scans",
                description: "Drag scanned JPEGs into Exiftrator. No upload — files stay on your device.",
              },
              {
                icon: <Calendar className="size-5" />,
                number: "02",
                title: "Open Calendar View",
                description: "See all photos on a visual timeline. Drag them to the correct original dates.",
              },
              {
                icon: <Clock className="size-5" />,
                number: "03",
                title: "Set time & export",
                description: "Assign accurate times, then export. The corrected EXIF dates are baked in.",
              },
            ].map((step, i) => (
              <div key={step.number} className="relative text-center group">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border-2 border-border-default bg-bg-secondary mb-5 transition-all duration-300 group-hover:border-accent-primary group-hover:shadow-md">
                  <span className="font-mono text-lg font-bold text-text-primary">
                    {step.number}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-text-primary mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-text-secondary leading-relaxed">
                  {step.description}
                </p>
                {i < 2 && (
                  <div className="hidden sm:block absolute top-7 right-0 translate-x-1/2">
                    <ArrowRight className="size-4 text-text-supporting" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Exiftrator ──────────────────────────────────────────── */}
      <section className="bg-bg-deep border-t border-border-default">
        <div className="max-w-5xl mx-auto px-6 py-16 sm:py-24">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-text-primary text-center mb-12">
            Why Exiftrator for scanned photos?
          </h2>

          <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="text-center p-5 rounded-xl border border-border-default bg-bg-secondary/50">
              <Calendar className="size-6 mx-auto mb-3 text-accent-primary" />
              <h3 className="text-sm font-semibold text-text-primary mb-1">Visual Calendar</h3>
              <p className="text-xs text-text-secondary">
                Drag photos to the correct date. No CLI commands, no guesswork.
              </p>
            </div>
            <div className="text-center p-5 rounded-xl border border-border-default bg-bg-secondary/50">
              <ShieldCheck className="size-6 mx-auto mb-3 text-green-800 dark:text-green-200" />
              <h3 className="text-sm font-semibold text-text-primary mb-1">100% Private</h3>
              <p className="text-xs text-text-secondary">
                Nothing is uploaded. All processing happens in your browser.
              </p>
            </div>
            <div className="text-center p-5 rounded-xl border border-border-default bg-bg-secondary/50">
              <Clock className="size-6 mx-auto mb-3 text-amber-800 dark:text-amber-200" />
              <h3 className="text-sm font-semibold text-text-primary mb-1">Batch Time Assign</h3>
              <p className="text-xs text-text-secondary">
                Select multiple photos and assign times in one operation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────── */}
      <section className="bg-bg-primary border-t border-border-default">
        <div className="max-w-5xl mx-auto px-6 py-16 sm:py-24 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-text-primary mb-4">
            Fix your scanned photo dates now
          </h2>
          <p className="text-text-secondary mb-8 max-w-md mx-auto">
            Free, private, and instant. No account required.
          </p>
          <Link
            to="/app"
            className="inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold rounded-lg bg-primary text-primary-foreground transition-all duration-200 hover:opacity-90 hover:gap-3 shadow-md"
          >
            Launch Web App — Free
            <ArrowRight className="size-4" />
          </Link>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer className="border-t border-border-default bg-bg-deep">
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={theme === "dark" ? logoDark : logoLight}
              alt="Exiftrator"
              className="h-5 w-auto opacity-60"
            />
          </Link>
          <p className="text-xs text-text-supporting">
            Privacy by design. Built for photographers.
          </p>
        </div>
      </footer>
    </div>
  );
}
